import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { billingApi, BillingOrder, TimelineEvent } from '../../lib/api/billing';
import { ArrowLeft, Calendar, Download, User, Mail, ExternalLink, Package } from 'lucide-react';
import { ErrorStatusPage } from '../../components/ErrorStatusPage';

const formatCurrency = (amount: number, currency: string) => {
    // Amount is stored in Cents (e.g. 2200), so divide by 100
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100);
};

export const OrderDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [order, setOrder] = useState<BillingOrder | null>(null);
    const [events, setEvents] = useState<TimelineEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showRefundModal, setShowRefundModal] = useState(false);
    const [refundAmount, setRefundAmount] = useState<string>('');
    const [refundReason, setRefundReason] = useState<string>('requested_by_customer');
    const [isRefunding, setIsRefunding] = useState(false);
    const [refundError, setRefundError] = useState<string | null>(null);

    useEffect(() => {
        if (id) {
            loadOrder(id);
        }
    }, [id]);

    const loadOrder = async (orderId: string) => {
        try {
            setLoading(true);
            setError(null);
            const data = await billingApi.getOrder(orderId);
            setOrder(data);

            // Fetch timeline
            try {
                const timeline = await billingApi.getOrderTimeline(orderId);
                setEvents(timeline);
            } catch (e) {
                console.warn('Failed to load timeline', e);
            }
        } catch (err: any) {
            // Use friendly error message
            if (err.response?.status === 404) {
                setError('not_found');
            } else if (err.response?.status >= 500 || !err.response) {
                setError('service_unavailable');
            } else {
                setError('generic');
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleRefund = async () => {
        try {
            setIsRefunding(true);
            setRefundError(null);
            
            const amountInCents = refundAmount ? Math.round(parseFloat(refundAmount) * 100) : undefined;
            
            await billingApi.refundOrder(id!, amountInCents, refundReason);
            setShowRefundModal(false);
            loadOrder(id!);
        } catch (e: any) {
            setRefundError(e.response?.data?.error || e.message || t('orders.refund.failed'));
        } finally {
            setIsRefunding(false);
        }
    };

    if (loading) return <div className="p-10 flex justify-center text-neutral-500">Loading...</div>;

    if (error || !order) {
        return (
            <ErrorStatusPage
                type={(error as 'service_unavailable' | 'not_found' | 'generic') || 'not_found'}
                onRetry={id ? () => loadOrder(id) : undefined}
            />
        );
    }

    type TimelineItem = {
        date: Date;
        title: string;
        description?: string | null;
        time: string;
    };

    const timelineItems: TimelineItem[] = events.length > 0 ? events.map(e => ({
        date: new Date(e.createdAt),
        title: e.title,
        description: e.description,
        time: new Date(e.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
    })) : [
        {
            date: new Date(order.date),
            title: `Order #${order.orderNumber} placed`,
            description: undefined,
            time: new Date(order.date).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
        }
    ];

    if (events.length === 0 && order.status === 'PAID') {
        timelineItems.unshift({
            date: new Date(order.date),
            title: `Payment of ${formatCurrency(order.amount, order.currency)} was processed${order.paymentMethod ? ` on ${order.paymentMethod}` : ''}.`,
            description: undefined,
            time: new Date(order.date).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
        });
    }

    return (
        <div className="h-full flex flex-col p-1 animate-fade-in max-w-5xl mx-auto w-full">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/payments/orders')}
                        className="btn-ghost !p-1.5"
                    >
                        <ArrowLeft size={16} />
                    </button>
                    <div className="flex items-center gap-3">
                        <h1 className="text-xl font-bold text-neutral-900">Order #{order.orderNumber}</h1>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${order.status === 'PAID' ? 'bg-green-50 text-green-700 border-green-200' :
                            order.status === 'FAILED' ? 'bg-red-50 text-red-700 border-red-200' :
                                'bg-neutral-100 text-neutral-600 border-neutral-200'
                            }`}>
                            {order.status}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-neutral-500">
                        <Calendar size={14} />
                        {new Date(order.date).toLocaleString([], { dateStyle: 'long', timeStyle: 'short' })}
                    </div>
                    {['PAID', 'PARTIALLY_REFUNDED'].includes(order.status) && (
                        <button 
                            onClick={() => setShowRefundModal(true)}
                            className="bg-white border border-red-200 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
                        >
                            {t('orders.refund.button')}
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

                {/* Main Column (Left) */}
                <div className="lg:col-span-2 space-y-6">

                    {/* Order Items / Plan */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50">
                            <h2 className="text-sm font-semibold text-neutral-900">{t('orders.details')}</h2>
                        </div>
                        <div className="p-0">
                            <div className="flex items-center justify-between p-4 border-b border-neutral-100 last:border-0">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center text-blue-600">
                                        <Package size={20} />
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium text-neutral-900 hover:text-blue-600 cursor-pointer transition-colors">
                                            {order.planName || 'Subscription Product'}
                                        </div>
                                        <div className="text-xs text-neutral-500">
                                            {order.type === 'INITIAL' ? 'Initial Subscription' : 'Renewal'}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-sm font-medium text-neutral-900">
                                    {formatCurrency(order.amount, order.currency)}
                                </div>
                            </div>

                            <div className="bg-neutral-50/30 p-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-neutral-500">Subtotal</span>
                                    <span className="text-neutral-900">{formatCurrency(order.amount, order.currency)}</span>
                                </div>
                                <div className="flex justify-between text-sm font-medium pt-2 border-t border-neutral-200">
                                    <span className="text-neutral-900">Total</span>
                                    <span className="text-neutral-900">{formatCurrency(order.amount, order.currency)}</span>
                                </div>
                                <div className="flex justify-between text-xs text-neutral-500 pt-1">
                                    <span>Paid by customer</span>
                                    <span>{formatCurrency(order.amount, order.currency)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50">
                            <h2 className="text-sm font-semibold text-neutral-900">{t('orders.timeline')}</h2>
                        </div>
                        <div className="p-6">
                            <div className="space-y-8 relative">
                                <div className="absolute top-2 bottom-2 left-[19px] w-0.5 bg-neutral-100 z-0"></div>
                                {timelineItems.map((event, idx) => (
                                    <div key={idx} className="relative flex gap-4 z-10">
                                        <div className="w-10 flex-shrink-0 flex justify-center">
                                            <div className="w-3 h-3 rounded-full bg-neutral-400 border-2 border-white ring-1 ring-neutral-200 mt-1.5"></div>
                                        </div>
                                        <div className="flex-1 flex justify-between items-start group">
                                            <div className="max-w-[80%]">
                                                <div className="text-sm text-neutral-900 font-medium">
                                                    {event.title}
                                                </div>
                                                {event.description && (
                                                    <div className="text-xs text-neutral-500 mt-0.5">
                                                        {event.description}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-xs text-neutral-400 whitespace-nowrap">
                                                {event.time}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <div className="relative flex gap-4 z-10">
                                    <div className="w-10 flex-shrink-0 flex justify-center">
                                        <div className="w-2 h-2 rounded-full bg-neutral-200 mt-2"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Sidebar (Right) */}
                <div className="space-y-6">

                    {/* Customer Card */}
                    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50">
                            <h2 className="text-sm font-semibold text-neutral-900">{t('orders.customer')}</h2>
                        </div>
                        <div className="p-4">
                            {order.userName && (
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-500">
                                        <User size={14} />
                                    </div>
                                    <button
                                        onClick={() => navigate(`/customers/${order.userId}`)}
                                        className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                                    >
                                        {order.userName}
                                    </button>
                                </div>
                            )}

                            <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5"><Mail size={14} className="text-neutral-400" /></div>
                                    <div>
                                        <div className="text-xs font-medium text-neutral-900">{t('orders.contactInfo')}</div>
                                        <a href={`mailto:${order.userEmail}`} className="text-xs text-blue-600 hover:underline break-all">
                                            {order.userEmail}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Invoice Card */}
                    {order.hostedInvoiceUrl && (
                        <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                            <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/50">
                                <h2 className="text-sm font-semibold text-neutral-900">{t('orders.invoice')}</h2>
                            </div>
                            <div className="p-4">
                                <a
                                    href={order.hostedInvoiceUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center justify-between p-2 border border-neutral-200 rounded hover:bg-neutral-50 transition-colors text-sm"
                                >
                                    <span className="font-medium text-neutral-700">{t('orders.viewInvoice')}</span>
                                    <ExternalLink size={14} className="text-neutral-400" />
                                </a>
                                {order.invoicePdf && (
                                    <a
                                        href={order.invoicePdf}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-2 mt-3 text-xs text-neutral-500 hover:text-neutral-900"
                                    >
                                        <Download size={12} />
                                        {t('orders.downloadPdf')}
                                    </a>
                                )}
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* Refund Modal */}
            {showRefundModal && order && (
                <div className="fixed inset-0 bg-neutral-900/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95">
                        <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-neutral-900">{t('orders.refund.modalTitle')}</h3>
                            <button onClick={() => setShowRefundModal(false)} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">
                                ×
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            {refundError && (
                                <div className="p-3 bg-red-50 text-red-700 text-sm rounded border border-red-200">
                                    {refundError}
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">{t('orders.refund.reason')}</label>
                                <select 
                                    className="w-full border border-neutral-300 rounded-md shadow-sm px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 outline-none"
                                    value={refundReason}
                                    onChange={(e) => setRefundReason(e.target.value)}
                                >
                                    <option value="requested_by_customer">{t('orders.refund.reasons.requested_by_customer')}</option>
                                    <option value="duplicate">{t('orders.refund.reasons.duplicate')}</option>
                                    <option value="fraudulent">{t('orders.refund.reasons.fraudulent')}</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">
                                    {t('orders.refund.amount')}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-neutral-500 sm:text-sm">$</span>
                                    </div>
                                    <input
                                        type="number"
                                        step="0.01"
                                        placeholder={`Full amount: ${(order.amount / 100).toFixed(2)}`}
                                        className="w-full border border-neutral-300 rounded-md shadow-sm pl-7 pr-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 outline-none"
                                        value={refundAmount}
                                        onChange={(e) => setRefundAmount(e.target.value)}
                                    />
                                </div>
                                <p className="mt-1 text-xs text-neutral-500">{t('orders.refund.fullAmountHint')}</p>
                            </div>
                            <div className="pt-4 flex justify-end gap-3">
                                <button
                                    onClick={() => setShowRefundModal(false)}
                                    className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
                                    disabled={isRefunding}
                                >
                                    {t('orders.refund.cancel')}
                                </button>
                                <button
                                    onClick={handleRefund}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
                                    disabled={isRefunding}
                                >
                                    {isRefunding ? t('orders.refund.processing') : t('orders.refund.confirm')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
